import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import { useSelector } from 'react-redux'
import Button from '../button'
import NumberStepperInput from '../number-stepper-input'
import {
    MM_PRESETS,
    MM_MIN_MINUTES,
    clampDurationMinutes,
    formatDurationLabel
} from './constants'

const POSITIVE_INT_RE = /^[1-9]\d*$/

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        margin: '12px 0 4px'
    },
    pills: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 8
    },
    pill: {
        '&&': {
            height: 'auto',
            minWidth: 0,
            padding: '9px 16px',
            borderRadius: 999,
            fontSize: 13
        }
    },
    stepper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12
    },
    field: {
        '&&': { width: 90 },
        '& input': {
            textAlign: 'center',
            fontWeight: 600
        }
    },
    unit: { color: ({ t }) => t.textSecondary, fontSize: 14 },
    errorText: { color: ({ t }) => t.error, fontSize: 12, textAlign: 'center' }
})

export const MaintenanceDurationPicker = ({
    value,
    onChange,
    presets = MM_PRESETS,
    label,
    onValidityChange
}) => {
    const t = useSelector(state => state.main.theme)
    const locale = useSelector(state => state.main.locale)
    const classes = useStyles({ t })
    const isPreset = presets.includes(value)
    const [userChoseCustom, setUserChoseCustom] = useState(!isPreset)
    const customOpen = userChoseCustom || !isPreset

    const step = delta => onChange(clampDurationMinutes(value + delta))

    const [draft, setDraft] = useState(String(value))
    const [invalid, setInvalid] = useState(false)
    useEffect(() => {
        setDraft(String(value))
        setInvalid(false)
    }, [value])

    const onFieldChange = raw => {
        setInvalid(false)
        setDraft(raw)
        if (raw === '') return
        onChange(clampDurationMinutes(raw))
    }

    const commitField = () => {
        setInvalid(false)
        if (draft === '') return
        onChange(clampDurationMinutes(Number(draft)))
    }

    const isComplete = !customOpen || draft !== ''
    useEffect(() => {
        if (onValidityChange) onValidityChange(isComplete)
    }, [isComplete, onValidityChange])

    return (
        <div
            className={classes.root}
            role="group"
            aria-label={label || locale.common.maintenanceMode}>
            <div className={classes.pills}>
                {presets.map(p => {
                    const selected = !userChoseCustom && value === p
                    return (
                        <Button
                            key={p}
                            reverted={!selected}
                            aria-pressed={selected}
                            className={classes.pill}
                            onClick={() => {
                                setUserChoseCustom(false)
                                onChange(p)
                            }}>
                            {formatDurationLabel(p)}
                        </Button>
                    )
                })}
                <Button
                    reverted={!customOpen}
                    aria-pressed={customOpen}
                    className={classes.pill}
                    onClick={() => setUserChoseCustom(true)}>
                    {locale.common.maintenanceDurationCustom}
                </Button>
            </div>

            {customOpen && (
                <div className={classes.stepper}>
                    <NumberStepperInput
                        size="small"
                        value={draft}
                        setValue={onFieldChange}
                        pattern={POSITIVE_INT_RE}
                        onInvalidChange={() => setInvalid(true)}
                        onStep={step}
                        decrementDisabled={value <= MM_MIN_MINUTES}
                        decrementLabel={
                            locale.common.maintenanceDurationDecrease
                        }
                        incrementLabel={
                            locale.common.maintenanceDurationIncrease
                        }
                        onBlur={commitField}
                        isError={invalid}
                        className={classes.field}
                        inputProps={{
                            'aria-label':
                                locale.common.maintenanceDurationMinutes
                        }}
                    />
                    <span className={classes.unit}>
                        {locale.common.maintenanceDurationMinUnit}
                    </span>
                </div>
            )}
            {customOpen && invalid && (
                <div className={classes.errorText}>
                    {locale.common.maintenanceDurationPositiveOnly}
                </div>
            )}
        </div>
    )
}

export default MaintenanceDurationPicker
